import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import "../public-themes.css";
import "../public-motion.css";
import { useRevealAll } from "../lib/motion";

export const THEMES = ["modern", "luxury", "classic", "bold"];
const THEME_FONTS = {
  luxury: "family=Cormorant+Garamond:wght@400;500;600&family=Amiri:wght@400;700",
  classic: "family=Libre+Baskerville:wght@400;700&family=Noto+Naskh+Arabic:wght@400;600",
  bold: "family=Archivo:wght@500;800&family=Cairo:wght@500;800",
};
// Loads only the active theme's display fonts, without blocking the first paint.
function useThemeFonts(theme) {
  useEffect(() => {
    const q = THEME_FONTS[theme];
    if (!q || document.getElementById(`tf-${theme}`)) return;
    const l = document.createElement("link");
    l.id = `tf-${theme}`; l.rel = "stylesheet"; l.href = `https://fonts.googleapis.com/css2?${q}&display=optional`;
    document.head.appendChild(l);
  }, [theme]);
}
import PoweredBy from "./PoweredBy";
import ChatWidget from "./ChatWidget";
import { publicApi } from "../lib/publicApi";
import { useT } from "../lib/i18n";
import usePublicSettings from "../lib/usePublicSettings";
import { BrandLogo } from "./BrandLogo";

import { tr } from "../lib/i18n";
function PublicChatMount() {
  const loc = useLocation();
  const [ctx, setCtx] = useState({ id: null, label: null });
  useEffect(() => {
    const m = loc.pathname.match(/^\/cars\/([^/?#]+)/);
    if (!m) { setCtx({ id: null, label: null }); return; }
    publicApi.get(`/vehicles/${m[1]}`)
      .then((r) => setCtx({ id: r.data.id, label: `${r.data.stock_number} · ${r.data.make} ${r.data.model} ${r.data.model_year}` }))
      .catch(() => setCtx({ id: null, label: null }));
  }, [loc.pathname]);
  return <ChatWidget vehicleId={ctx.id} vehicleLabel={ctx.label} />;
}

export default function PublicLayout({ children }) {
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const { settings } = usePublicSettings();
  useRevealAll();
  const previewTheme = (() => {
    const q = new URLSearchParams(loc.search).get("preview_theme");
    const framed = window.self !== window.top;
    try {
      if (q && framed) sessionStorage.setItem("preview_theme", q);
      return q || (framed ? sessionStorage.getItem("preview_theme") : null);
    } catch { return q; }
  })();
  const theme = THEMES.includes(previewTheme) ? previewTheme : (THEMES.includes(settings?.theme) ? settings.theme : "modern");
  useThemeFonts(theme);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const name = settings?.showroom_name || tr("x.showroom");
  const waNumber = (settings?.whatsapp || "").replace(/[^\d]/g, "");
  const [carWa, setCarWa] = useState(null);
  useEffect(() => {
    const f = (e) => setCarWa(e.detail);
    window.addEventListener("pub-wa", f);
    return () => window.removeEventListener("pub-wa", f);
  }, []);
  const fabNumber = carWa?.number || waNumber;
  const fabHref = `https://wa.me/${fabNumber}${carWa?.text ? `?text=${encodeURIComponent(carWa.text)}` : ""}`;
  const ig = (settings?.instagram || "").trim();
  const igUrl = !ig ? "" : ig.startsWith("http") ? ig : `https://instagram.com/${ig.replace(/^@/, "")}`;
  const year = new Date().getFullYear();
  const transparent = loc.pathname === "/";
  const darkLogo = settings?.logo_dark_url || settings?.logo_light_url;
  const onWhiteHeader = !transparent || scrolled || theme === "classic";
  const headerLogo = onWhiteHeader ? (settings?.logo_light_url || settings?.logo_dark_url) : darkLogo;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) => (isActive ? "active" : "");
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={`public-shell ${menuOpen ? "menu-open" : ""}`} data-theme={theme} data-testid="public-shell">
      <header className={`public-header v2 ${transparent ? "transparent" : ""} ${scrolled ? "scrolled" : ""}`}>
        <div className="public-container public-nav">
          <button data-testid="public-brand" className="public-brand" onClick={() => nav("/")}>
            <BrandLogo src={headerLogo} name={name} testid="public-header" nameClassName="brand-fallback" />
          </button>
          <button className="mobile-menu-btn" onClick={() => setMenuOpen((v) => !v)}
            data-testid="public-menu-toggle" aria-label={tr("x.menu")}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <nav className={`public-nav-links ${menuOpen ? "open" : ""}`}>
            <NavLink to="/" className={linkClass} onClick={closeMenu} end data-testid="pub-nav-home">{t("nav.home")}</NavLink>
            <NavLink to="/cars" className={linkClass} onClick={closeMenu} data-testid="pub-nav-cars">{t("nav.cars")}</NavLink>
            <NavLink to="/sell" className={linkClass} onClick={closeMenu} data-testid="pub-nav-sell">{t("nav.sell")}</NavLink>
            <NavLink to="/finance" className={linkClass} onClick={closeMenu} data-testid="pub-nav-finance">{t("px.finance")}</NavLink>
            <NavLink to="/about" className={linkClass} onClick={closeMenu} data-testid="pub-nav-about">{t("nav.about")}</NavLink>
            <NavLink to="/contact" className={linkClass} onClick={closeMenu} data-testid="pub-nav-contact">{t("nav.contact")}</NavLink>
          </nav>
          <div className="public-nav-actions">
            <button className="pub-lang" data-testid="pub-lang-switch" onClick={() => setLang(lang === "en" ? "ar" : "en")}>
              {lang === "en" ? "عربي" : "EN"}
            </button>
            {waNumber ? (
              <a className="pub-cta primary" href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="pub-cta-whatsapp">
                {t("px.whatsapp_us")}
              </a>
            ) : null}
          </div>
        </div>
      </header>

      <main className="pub-main">{children}</main>

      <PublicChatMount />

      {/* Footer v2 (dark) */}
      <footer className="pub-footer-v2" data-testid="pub-footer">
        <div className="public-container footer-cols">
          <div className="col-brand">
            <BrandLogo src={darkLogo} name={name} testid="footer" imgClassName="footer-logo" nameClassName="footer-name" nameStyle={{ display: "block", fontWeight: 600, fontSize: 18, marginBottom: 12 }} />
            {settings?.tagline ? <p dir="auto" style={{ fontWeight: 600 }} data-testid="footer-tagline">{settings.tagline}</p> : null}
            <p dir="auto" className="clamp">{settings?.about_text || t("pub.footer_about")}</p>
          </div>
          <div className="col">
            <h2>{t("px.explore")}</h2>
            <ul>
              <li><a href="/cars">{t("nav.cars")}</a></li>
              <li><a href="/sell">{t("nav.sell")}</a></li>
              <li><a href="/finance">{t("px.finance")}</a></li>
              <li><a href="/about">{t("nav.about")}</a></li>
            </ul>
          </div>
          {settings?.branches?.length > 1 && (
            <div className="col" data-testid="footer-branches">
              <h2>{tr("br.our_branches")}</h2>
              <ul>
                {settings.branches.map((b) => (
                  <li key={b.id}>
                    {b.map_url ? <a href={b.map_url} target="_blank" rel="noopener noreferrer">{b.name}</a> : b.name}
                    {b.phone ? <> · <a href={`tel:${b.phone}`}><span dir="ltr" className="ltr">{b.phone}</span></a></> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="col">
            <h2>{t("px.reach_us")}</h2>
            <ul>
              {settings?.phone ? <li><a href={`tel:${settings.phone}`}><span dir="ltr" className="ltr">{settings.phone}</span></a></li> : null}
              {waNumber ? <li><a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></li> : null}
              {settings?.email ? <li><a href={`mailto:${settings.email}`}>{settings.email}</a></li> : null}
              {settings?.address ? <li className="muted">{settings.address}</li> : null}
              <li><a href="/admin" data-testid="footer-staff-login">{t("px.staff_login")}</a></li>
            </ul>
          </div>
          {(settings?.facebook || igUrl || settings?.twitter) ? <div className="col">
            <h2>{t("px.social")}</h2>
            <ul>
              {settings?.facebook ? <li><a href={settings.facebook} target="_blank" rel="noopener noreferrer">{tr("x.facebook")}</a></li> : null}
              {igUrl ? <li><a href={igUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram">{tr("x.instagram")}</a></li> : null}
              {settings?.twitter ? <li><a href={settings.twitter} target="_blank" rel="noopener noreferrer">X</a></li> : null}
            </ul>
          </div> : null}
        </div>
        <div className="pub-footer-strip">
          <div className="public-container">
            <span>© {year} {name}. {t("footer.rights")}</span>
            <PoweredBy variant="dark" />
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      {fabNumber ? (
        <a className="pub-wa-fab" href={fabHref} target="_blank" rel="noopener noreferrer" data-testid="pub-wa-float" aria-label={tr("br.whatsapp")}>
          <MessageCircle size={22} strokeWidth={2} />
        </a>
      ) : null}
    </div>
  );
}
